import React, { useState, useEffect } from 'react';
import {
    Box,
    useTheme,
    useMediaQuery,
    Typography,
} from '@mui/material';
import ChallengeItem, { DailyCountChallengeItem } from '../../../../components/challengeItem';
import { challengeAPI } from '../../../../actions/imsServer';
import { API_PAGE_SIZE } from '../../../../constants';
import { useNavigate } from 'react-router';

export const ChallengeTab = ({ userData }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    // 실제 데이터 상태
    const [challengeProgressData, setChallengeProgressData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 실제 데이터 가져오기
    const fetchChallengeData = async () => {
        const targetUserId = userData?.id;
        if (!targetUserId) return;
        try {
            setLoading(true);
            setError(null);

            let allProgresses = [];
            let offset = 0;
            let page;
            do {
                const res = await challengeAPI.getChallengeProgress({
                    userId: targetUserId,
                    state: 0,
                    expand: 'challenge',
                    offset
                });
                page = res?.data?.challengeProgresses || [];
                allProgresses = allProgresses.concat(page);
                offset += API_PAGE_SIZE;
            } while (page.length === API_PAGE_SIZE);

            setChallengeProgressData(allProgresses);
        } catch (err) {
            console.error('챌린지 데이터 로딩 오류:', err);
            setError(err.message);
            setChallengeProgressData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userData?.id) {
            fetchChallengeData();
        }
    }, [userData?.id]);


    if (loading) {
        return (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Typography>로딩 중...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Typography color="error">오류: {error}</Typography>
            </Box>
        );
    }

    if (!challengeProgressData || challengeProgressData.length === 0) {
        return (
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <Typography sx={{ color: theme.palette.figma.white[60] }}>
                    챌린지 데이터가 없습니다.
                </Typography>
            </Box>
        );
    }

    // 타입별로 분리
    const leftChallenges = challengeProgressData.filter(cp => cp.challenge.type === 0 || cp.challenge.type === 1);
    const rightChallenges = challengeProgressData.filter(cp => cp.challenge.type === 2 || cp.challenge.type === 3);

    const renderChallenge = (challengeProgress, index) => {
        const challenge = challengeProgress.challenge;
        const isSimpleChallenge = challenge.targetCount.length === 1 && challenge.targetCount[0] === 1 || challenge.targetCount.length > 1;

        // 진행 상태가 없으면 기본값으로 빈 카드 표시
        const displayValue = challengeProgress?.value ?? 0;
        const completedRewardPoints = challengeProgress?.rewardPoints ?? 0;
        const completedCount = challengeProgress?.completedCount ?? 0;
        const completedDates = challengeProgress?.completedDates ?? [];
        const lastDate = challengeProgress?.lastDate ?? null;

        return isSimpleChallenge ? (
            <ChallengeItem
                key={`challenge-${index}`}
                type={challenge.type}
                nameKey={challenge.name}
                value={displayValue}
                targetValue={challenge.targetValue}
                rewardPoint={challenge.rewardPoint}
                period={challenge.period}
                targetCount={challenge.targetCount}
                completedCount={completedCount}
                completedRewardPoints={completedRewardPoints}
                lastDate={lastDate}
                user={userData}
            />
        ) : (
            <DailyCountChallengeItem
                key={`daily-challenge-${index}`}
                type={challenge.type}
                period={challenge.period}
                nameKey={challenge.name}
                value={displayValue}
                completedCount={completedCount}
                completedDates={completedDates}
                targetCount={challenge.targetCount}
                targetValue={challenge.targetValue}
                rewardPoint={challenge.rewardPoint}
                lastDate={lastDate}
            />
        );
    };

    return (
        <Box sx={{ flex: 1, display: 'flex', gap: 2 }}>
            {/* 좌측 컬럼 - type 0, 1 */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {leftChallenges.map((challengeProgress, index) =>
                    renderChallenge(challengeProgress, `left-${index}`)
                )}
            </Box>

            {/* 우측 컬럼 - type 2 */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {rightChallenges.map((challengeProgress, index) =>
                    renderChallenge(challengeProgress, `right-${index}`)
                )}
            </Box>
        </Box>
    );
};
